import React, { useState, useEffect } from 'react';
import UserFilter from './UserFilter';
import defaultAvatar from '../../assets/images/default_avatar.svg';
import Image from '../Image';

interface User {
  id: number;
  uuid: string;
  firstName: string;
  lastName: string;
  role?: string;
  country: string;
  ethnicities: string[];
  gender: string;
  height: number;
  weight: number;
  avatarUrl: string;
  croppedAvatarUrl: string;
  measure_system: 'imperial' | 'metric';
  subscription_status?: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete';
  subscription_tier?: 'free' | 'pro' | 'premium';
}

interface CurrentUser extends User {
  super_admin: boolean;
}

interface Group {
  id: number;
  title: string;
  status?: 'Active' | 'Archived';
  members?: string[];
}

interface FavoritePageProps {
  currentUser: CurrentUser;
}

interface GroupMenuProps {
  user: User;
  groups: Group[];
  onAddToGroup: (groupId: number) => void;
  onOpenCreateModal: () => void;
  onClose: () => void;
  isCreating: boolean;
}

function GroupContextMenu({ user, groups, onAddToGroup, onOpenCreateModal, onClose, isCreating }: GroupMenuProps) {
  const activeGroups = groups.filter(g => (g.status === 'Active' || !g.status) && (!g.members || g.members.indexOf(user.uuid) < 0)); 

  return (
    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="p-3 border-b border-gray-100">
        <div className="text-sm font-medium text-gray-900">
          Add {user.firstName} to Collection
        </div>
      </div>
      
      <div className="p-3">
        <select 
          value=""
          onChange={(e) => {
            if (e.target.value) {
              onAddToGroup(parseInt(e.target.value));
            }
          }}
          disabled={isCreating}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {activeGroups.length > 0 ? "Select a collection to add to..." : "No active collections"}
          </option>
          {activeGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.title}
            </option>
          ))}
        </select>
      </div>

      <div className="border-t border-gray-100 p-3">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenCreateModal();
            onClose();
          }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New Collection
        </button>
      </div>

      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function FavoritePage({ currentUser }: FavoritePageProps) {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [favoriteUsers, setFavoriteUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [showGroupMenu, setShowGroupMenu] = useState<string | null>(null);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [selectedUserForGroup, setSelectedUserForGroup] = useState<string | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    project_type: '',  
    status: 'Active' as const,
    start_date: '',
    due_date: '',
    hashtag: '',
    member_ids: [] as string[]
  });

  const isSuperAdmin = currentUser.super_admin;

  const measurementRanges = {
    height: { min: 150, max: 200 },
    weight: { min: 45, max: 120 }
  };

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await fetch('/api/v1/favorites/users', {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        });

        if (response.ok) {
          const data = await response.json();
          const users: User[] = data.users || [];
          setFavoriteUsers(users);
          setFilteredUsers(users);

          const favoriteUuids = new Set(users.map(u => u.uuid));
          setFavorites(favoriteUuids);
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch('/groups/groups_json', { 
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setGroups(data);
      } catch (err) {
        console.error('Error fetching groups:', err);
        setGroups([]);
      }
    };

    fetchGroups();
  }, []);

  const toggleFavorite = async (userUuid: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const isFavorited = favorites.has(userUuid);
    const method = isFavorited ? 'DELETE' : 'POST';
    const url = isFavorited ? `/api/v1/favorites/${userUuid}` : '/api/v1/favorites';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: method === 'POST' ? JSON.stringify({ member_uuid: userUuid }) : undefined
      });

      if (response.ok) {
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          if (isFavorited) {
            newFavorites.delete(userUuid);
            setFavoriteUsers(prevUsers => prevUsers.filter(user => user.uuid !== userUuid));
            setFilteredUsers(prevUsers => prevUsers.filter(user => user.uuid !== userUuid));
          } else {
            newFavorites.add(userUuid);
          }
          return newFavorites;
        });
      } else {
        console.error('Error toggling favorite:', response.statusText);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleAddToGroup = async (userUuid: string, groupId: number) => {
    setIsCreatingGroup(true);
    try {
      const response = await fetch(`/groups/${groupId}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ user_uuid: userUuid })
      });

      if (response.ok) {
        const user = favoriteUsers.find(u => u.uuid === userUuid);
        alert(`${user?.firstName} ${user?.lastName} added to collection!`);
      } else {
        const data = await response.json();
        alert((data.error || 'Unable to add member'));
      }
    } catch (error) {
      console.error('Error adding to group:', error);
      alert('Something went wrong while adding the member.');
    } finally {
      setIsCreatingGroup(false);
      setShowGroupMenu(null);
    }
  };

  const openCreateModal = (userUuid?: string) => {
    setSelectedUserForGroup(userUuid || null);
    setForm({
      title: '',
      description: '',
      project_type: '',  
      status: 'Active',
      start_date: '',
      due_date: '',
      hashtag: '',
      member_ids: userUuid ? [userUuid] : []
    });
    setIsModalOpen(true);
  };

  const handleGroupIconClick = (userUuid: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowGroupMenu(showGroupMenu === userUuid ? null : userUuid);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formData = {
        group: {
          title: form.title,
          description: form.description,
          project_type: form.project_type,
          status: form.status,
          start_date: form.start_date,
          hashtag: form.hashtag,
          due_date: form.due_date
        },
        member_ids: form.member_ids
      };

      const response = await fetch('/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const groupsResponse = await fetch('/groups/groups_json', { 
          headers: { 
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          credentials: 'same-origin'
        });
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
        
        setIsModalOpen(false);
        alert('Collection created successfully!');
      } else {
        const errorData = await response.json();
        console.error('Failed to create group:', errorData);
        alert(`Failed to create collection: ${errorData.errors ? errorData.errors.join(', ') : 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert('An error occurred while creating the collection. Please try again.');
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setShowGroupMenu(null);
    };

    if (showGroupMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showGroupMenu]);

  if (loading) {
    return (
      <div className="w-full flex flex-1 mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-10">
        <div className="w-full max-w-6xl">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading favorites...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen flex justify-center px-4 sm:px-6 lg:px-8 pt-10">
      <div className="w-full max-w-6xl">
        <div className="flex items-center gap-20 sm:hidden mb-4">
          <button
            onClick={() => window.location.href = '/users/directory'}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Back to Directory"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">My Favorites</h1>
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.location.href = '/groups'}
                className="relative p-2 rounded-full hover:bg-blue-200 transition-colors"
                aria-label="View Collections"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-black"
                >
                  <path d="M3 7V5c0-1.1.9-2 2-2h2" />
                  <path d="M17 3h2c1.1 0 2 .9 2 2v2" />
                  <path d="M21 17v2c0 1.1-.9 2-2 2h-2" />
                  <path d="M7 21H5c-1.1 0-2-.9-2-2v-2" />
                  <rect width="7" height="5" x="7" y="7" rx="1" />
                  <rect width="7" height="5" x="10" y="12" rx="1" />
                </svg>
                <span className="absolute -top-1 -right-1 bg-red-500 text-black text-xs w-5 h-5 rounded-full flex items-center justify-center">
                </span>
              </button>
          </div>
        </div>

        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.location.href = '/users/directory'}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Directory
            </button>
            <span className="text-3xl leading-9 font-semibold text-gray-900">My Favorites</span>
          </div>

          <div className="flex items-center gap-4">
            {groups.length > 0 ? (
              <button
                onClick={() => window.location.href = '/groups'}
                className="relative p-2 rounded-full hover:bg-blue-200 transition-colors"
                aria-label="View Collections"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-black"
                >
                  <path d="M3 7V5c0-1.1.9-2 2-2h2" />
                  <path d="M17 3h2c1.1 0 2 .9 2 2v2" />
                  <path d="M21 17v2c0 1.1-.9 2-2 2h-2" />
                  <path d="M7 21H5c-1.1 0-2-.9-2-2v-2" />
                  <rect width="7" height="5" x="7" y="7" rx="1" />
                  <rect width="7" height="5" x="10" y="12" rx="1" />
                </svg>
                <span className="absolute -top-1 -right-1 bg-red-500 text-black text-xs w-5 h-5 rounded-full flex items-center justify-center">
                </span>
              </button>
            ) : (
             <button
                onClick={() => window.location.href = '/groups'}
                className="relative p-2 rounded-full hover:bg-blue-200 transition-colors"
                aria-label="View Collections"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-black"
                >
                  <path d="M3 7V5c0-1.1.9-2 2-2h2" />
                  <path d="M17 3h2c1.1 0 2 .9 2 2v2" />
                  <path d="M21 17v2c0 1.1-.9 2-2 2h-2" />
                  <path d="M7 21H5c-1.1 0-2-.9-2-2v-2" />
                  <rect width="7" height="5" x="7" y="7" rx="1" />
                  <rect width="7" height="5" x="10" y="12" rx="1" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {favoriteUsers.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No favorites yet</h3>
            <p className="text-gray-500 mb-4">Start adding members to your favorites from the directory</p>
            <button
              onClick={() => window.location.href = '/users/directory'}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200"
            >
              Browse Directory
            </button>
          </div>
        ) : (
          <>
            <hr className="mt-5 mb-5" />

            <UserFilter
              users={favoriteUsers}
              // measurementRanges={measurementRanges}
                openFilters={true}
                firstnameLastNameshow={false}
                keywordshow={true}
                userGroupshow={false}
              measurementRanges={{
                                height: {
                                  min: 91, // 3ft : 91.44cm
                                  max: 244, // 8ft: 243.84cm
                                },
                                weight: {
                                  min: 22, // 50:lbs = 22.6796kg
                                  max: 182, // 400:lbs = 181.437kg
                                },
                              }}               
              currentUser={currentUser}
              onFilter={setFilteredUsers}
              showFavoritesOnly={false}
              favorites={favorites}
            />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mt-4">
              {filteredUsers.map((user) => (
                <div key={user.uuid} className="flex-1 bg-white shadow-lg rounded-b-lg divide-y divide-gray-200 project-row relative">
                  <a href={`/users/${user.uuid}?from=favorites`} className="block">
                    <div className="user-avatar w-full h-0 overflow-hidden rounded-t-lg bg-gray-200 relative">
                      <Image
                        className="absolute top-0 d-block ml-auto mr-auto w-full h-full object-cover"
                        src={user.croppedAvatarUrl}
                        fallbackSrc={defaultAvatar}
                        alt=""
                      />

                      {/* Pro Badge */}
                      {user.subscription_status === 'active' && (user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && (
                        <div className="absolute top-2 right-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded-full">
                          PRO
                        </div>
                      )}

                      {/* Individual Favorite Heart Icon */}
                      <button
                        onClick={(e) => toggleFavorite(user.uuid, e)}
                        className="absolute bottom-2 right-2 p-1.5 transition-all duration-200 hover:scale-110 z-999 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:bg-white/90"
                        aria-label="Remove from favorites"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="red"
                          stroke="red"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-red-500"
                        >
                          <path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"/>
                        </svg>
                      </button>

                      {/* Group Icon - shows for all favorites */}
                      <button
                        onClick={(e) => handleGroupIconClick(user.uuid, e)}
                        className="absolute bottom-2 right-12 p-1.5 transition-all duration-200 hover:scale-110 z-999 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:bg-white/90"
                        aria-label="Add to collection"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-blue-600"
                        >
                          <path d="M3 7V5c0-1.1.9-2 2-2h2" />
                          <path d="M17 3h2c1.1 0 2 .9 2 2v2" />
                          <path d="M21 17v2c0 1.1-.9 2-2 2h-2" />
                          <path d="M7 21H5c-1.1 0-2-.9-2-2v-2" />
                          <rect width="7" height="5" x="7" y="7" rx="1" />
                          <rect width="7" height="5" x="10" y="12" rx="1" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex flex-col px-4 py-4 sm:px-6 mt-auto">
                      <span className="text-gray-900 font-medium">
                        {user.firstName} {user.lastName}
                      </span>
                      {user.role && <span className="text-sm text-gray-500">{user.role}</span>}
                    </div>
                  </a>

                  {/* Group Context Menu */}
                  {showGroupMenu === user.uuid && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <GroupContextMenu
                        user={user}
                        groups={groups}
                        onAddToGroup={(groupId) => handleAddToGroup(user.uuid, groupId)}
                        onOpenCreateModal={() => openCreateModal(user.uuid)}
                        onClose={() => setShowGroupMenu(null)}
                        isCreating={isCreatingGroup}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Create Group Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4 transform transition-transform duration-300 scale-95 hover:scale-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Create New Collection
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="title"
                  placeholder="Title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  required
                />
                <input
                  type="text"
                  name="hashtag"
                  placeholder="#tags"
                  value={form.hashtag}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                />
                <textarea
                  name="description"
                  placeholder="Description"
                  value={form.description}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  rows={3}
                />

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200"
                  >
                    Create Collection
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}